import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal, mergeAndStringifyEntityObjects } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as customFieldActions from '@redux/modules/customField'

import defaultFormState, { requiredFields } from '@models/customField'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { CustomFieldModel, CustomFieldRequestOptions } from '@models/types'

const stringifyObjectKeys: (keyof CustomFieldModel)[] = ['options']

type CreateCustomFieldParams = {
  customFieldParams: Partial<CustomFieldModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomFieldRequestOptions,
}

const createCustomField = (params: CreateCustomFieldParams) => {
  const { dispatch, customFieldParams, requestOptions } = params
  const { createCustomField: createFn } = customFieldActions

  const updatedParams = mergeAndStringifyEntityObjects({}, customFieldParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateCustomFieldParams = {
  customField: CustomFieldModel,
  customFieldParams: Partial<CustomFieldModel>,
  dispatch: AppDispatch,
  requestOptions?: CustomFieldRequestOptions,
}

const updateCustomField = (params: UpdateCustomFieldParams) => {
  const {
    dispatch, customField, customFieldParams, requestOptions,
  } = params
  const { updateCustomField: updateFn } = customFieldActions

  const updatedParams = mergeAndStringifyEntityObjects(customField, customFieldParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCustomFieldParams = {
  customField: DeleteParams<CustomFieldModel>,
  dispatch: AppDispatch,
}

const deleteCustomField = (params: DeleteCustomFieldParams) => {
  const { dispatch, customField } = params
  const { deleteCustomField: deleteFn } = customFieldActions

  return dispatch(deleteFn(customField))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCustomFieldForm(
  customField: Partial<CustomFieldModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const customFieldForm = useForm(
    defaultFormState,
    { entity: customField, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [customField.id, customField.cache_key],
  )

  return {
    ...customFieldForm,
  }
}

function useCustomField(initEntity: Partial<CustomFieldModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: customField }: { entity: CustomFieldModel} = useLatestEntity(initEntity, 'customFields')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, updating,
  } = useSelector(reduxState => reduxState.customFields)

  const fieldWidth = digObject(customField, 'options.width', 100)
  const optionValues = digObject(customField, 'options.option_values', [])

  return {
    callbacks: {
      createCustomField: (customFieldParams: Partial<CustomFieldModel>, entityOptions?: CustomFieldRequestOptions) => (
        createCustomField({ customFieldParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCustomField: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCustomFieldModal',
        payload: { customField },
      }),
      deleteCustomField: () => deleteCustomField({ dispatch, customField }),
      updateCustomField: (customFieldParams: Partial<CustomFieldModel>, entityOptions?: CustomFieldRequestOptions) => (
        updateCustomField({
          customField, customFieldParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    customField,
    deleting,
    fieldWidth,
    optionValues,
    updating,
  }
}

export type UseCustomFieldPayload = ReturnType<typeof useCustomField>

export default useCustomField
